


import React from 'react'

import { getErrorMessage, slugifySubdomain, validateSubdomain, getDomainSuffix } from 'utils'
import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery, useSubscriptionPlans, useInternalUsers } from 'hooks/api'
import { useForm, Controller } from 'react-hook-form'
import { COMPANY_RULES } from 'validation/company'

import { Popover, Stack, Typography } from '@mui/material'

import { Modal, Button, Input } from '@repo/ui'
import { ProfilePhotoField, SelectField, Search, SearchSelect, FilterField, EmailChipInput } from 'components'



interface CreateCompanyModalProps {
  open: boolean
  onClose: () => void
  refetch: () => void
}

interface CreateCompanyFormValues {
  name: string
  subdomain: string
  adminEmails: string[]
  managerIds: string[]
  logo: File | null
  priceId: string
}

export const CreateCompanyModal = (props: CreateCompanyModalProps) => {
  const { open, onClose = () => { }, refetch } = props

  const { data: plans = [], isLoading: isPricesLoading } = useSubscriptionPlans()

  const methods = useForm<CreateCompanyFormValues>({
    defaultValues: {
      name: '',
      subdomain: '',
      adminEmails: [],
      managerIds: [],
      logo: null,
      priceId: '',
    },
  })
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
  } = methods

  const watchedName = watch('name')

  // Suggest a subdomain from the company name (matches the backend slugify) until
  // the admin edits the subdomain field themselves.
  const [subdomainEdited, setSubdomainEdited] = React.useState(false)

  // Internal Vera staff that can be assigned as managers of the new company.
  // Search-select dropdown (VRG-246 follow-up): same shared component the filters
  // side panel uses for a searchable multi-select — Search trigger + Popover +
  // SearchSelect (see CompanyFilter) — not a bespoke checklist under the field.
  const { data: internalUsers } = useInternalUsers({ limit: 200 })
  const activeManagers = React.useMemo(
    () => (internalUsers?.data ?? []).filter((u) => u.active),
    [internalUsers],
  )
  const [managerAnchorEl, setManagerAnchorEl] = React.useState<HTMLElement | null>(null)
  const [managerPopoverOpen, setManagerPopoverOpen] = React.useState(false)
  const [managerSearch, setManagerSearch] = React.useState('')

  const { open: notification } = useSnackbar()

  const { create, isLoading } = useCompanyMutationsQuery({
    onSuccess: () => {
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)

      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const onSubmit = handleSubmit((data) => {
    const logoUpload = data.logo
      ? {
          file: data.logo,
          preview_url: URL.createObjectURL(data.logo),
          isUploaded: false,
        }
      : { file: null, preview_url: '', isUploaded: false }

    // First admin is the provisioning owner (WorkOS org owner); the rest are
    // invited as additional company admins after provisioning.
    const [primaryEmail, ...extraEmails] = data.adminEmails

    create({
      name: data.name,
      subdomain: data.subdomain.trim().toLowerCase() || undefined,
      logo: logoUpload,
      email: primaryEmail,
      additionalAdminEmails: extraEmails.length ? extraEmails : undefined,
      managerIds: data.managerIds.length ? data.managerIds : undefined,
      priceId: data.priceId || undefined,
    })
  })

  const handleClose = () => {
    onClose()
    setSubdomainEdited(false)
    setManagerSearch('')
    setManagerAnchorEl(null)
    setManagerPopoverOpen(false)
    reset({
      name: '',
      subdomain: '',
      adminEmails: [],
      managerIds: [],
      logo: null,
      priceId: '',
    })
  }

  return (
    <Modal
      title="Create company"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            loading={isLoading}
          >
            Create Company
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Controller
          name="logo"
          control={control}
          render={({ field, fieldState }) => (
            <ProfilePhotoField
              label="Company logo"
              variant="logo"
              value={field.value}
              onChange={(file) => field.onChange(file)}
              defaultInitials={watchedName?.charAt(0) || 'C'}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={COMPANY_RULES.name}
          render={({ field, fieldState }) => (
            <Input
              placeholder="Enter"
              label="Name"
              value={field.value}
              onValueChange={(v: string) => {
                field.onChange(v)
                if (!subdomainEdited) setValue('subdomain', slugifySubdomain(v))
              }}
              error={!!fieldState.error}
            />
          )}
        />

        <Controller
          name="subdomain"
          control={control}
          rules={{ validate: validateSubdomain }}
          render={({ field, fieldState }) => (
            <Input
              placeholder="acme"
              label="Subdomain"
              value={field.value}
              onValueChange={(v: string) => {
                setSubdomainEdited(true)
                field.onChange(v.toLowerCase())
              }}
              endAdornment={
                <Typography variant="body3" color="text.secondary" sx={{ whiteSpace: 'nowrap', pr: 2 }}>
                  {getDomainSuffix()}
                </Typography>
              }
              helperText={fieldState.error?.message || 'Auto-suggested from the name. Edit to choose your own.'}
              error={!!fieldState.error}
            />
          )}
        />

        <Controller
          name="adminEmails"
          control={control}
          rules={{
            validate: (v: string[]) =>
              (v && v.length > 0) || 'At least one admin email is required',
          }}
          render={({ field, fieldState }) => (
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ fontSize: 16 }}>
                Add admins
              </Typography>
              <EmailChipInput
                placeholder="admin@company.com"
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
              />
              <Typography
                variant="body3"
                color={fieldState.error ? 'error' : 'text.secondary'}
                sx={{ lineHeight: 1.5 }}
              >
                {fieldState.error?.message ||
                  'The first admin owns the company. Add more by comma, space, or Enter.'}
              </Typography>
            </Stack>
          )}
        />

        <Controller
          name="managerIds"
          control={control}
          render={({ field }) => {
            const selectedManagers = activeManagers.filter((u) => field.value.includes(u.id))

            const handleManagerClick = (event: React.MouseEvent) => {
              setManagerAnchorEl(event.currentTarget as HTMLElement)
              setManagerPopoverOpen(true)
            }
            const handleManagerClose = () => {
              setManagerAnchorEl(null)
              setManagerPopoverOpen(false)
              setManagerSearch('')
            }
            const handleManagerApply = (items: typeof activeManagers) => {
              handleManagerClose()
              field.onChange(items.map((u) => u.id))
            }

            return (
              <FilterField
                label="Add Vera managers"
                showClear={field.value.length > 0}
                onClear={() => field.onChange([])}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  onClick={handleManagerClick}
                  sx={{ cursor: 'pointer' }}
                >
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Search
                      value={managerSearch}
                      onChange={setManagerSearch}
                      onClear={() => setManagerSearch('')}
                      placeholder="Search internal users"
                      fullWidth
                      debounce={false}
                      sx={{ minWidth: 0 }}
                    />
                  </Stack>
                </Stack>
                <Popover
                  open={managerPopoverOpen}
                  anchorEl={managerAnchorEl}
                  onClose={handleManagerClose}
                  disableAutoFocus
                  disableEnforceFocus
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        p: '8px',
                        borderRadius: '16px',
                        mt: '6px',
                        width: managerAnchorEl ? managerAnchorEl.clientWidth : undefined,
                        overflowX: 'hidden',
                        backgroundImage: 'none',
                        backgroundColor: 'background.paper',
                      },
                    },
                  }}
                >
                  <SearchSelect
                    items={activeManagers}
                    selected={selectedManagers}
                    source="filters"
                    hoverGradient
                    searchParams={{ fields: ['first_name', 'last_name', 'email'], preview: 'owners' }}
                    hideSearch
                    externalSearch={managerSearch}
                    onApply={handleManagerApply}
                    onClear={handleManagerClose}
                  />
                </Popover>
                <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Internal Vera staff who manage this company. They are linked directly. No invite is sent.
                  {field.value.length > 0 && ` · ${field.value.length} selected`}
                </Typography>
              </FilterField>
            )
          }}
        />

        <Controller
          name="priceId"
          control={control}
          render={({ field }) => (
            <SelectField
              label="Plan"
              value={field.value}
              onChange={field.onChange}
              disabled={isPricesLoading}
              options={[
                ...plans.filter((p) => p.interval !== 'year'),
                ...plans.filter((p) => p.interval === 'year'),
              ].map((plan) => {
                // `amount` mirrors Stripe `unit_amount`, which is always in cents.
                const dollars = plan.amount / 100
                return {
                  label: plan.title,
                  rightLabel: `$${dollars.toFixed(2)} / ${plan.interval}`,
                  value: plan.stripe_price_id,
                  group: plan.interval === 'year' ? 'Annual' : 'Monthly',
                }
              })}
            />
          )}
        />
      </Stack>
    </Modal>
  )
}
